/* global fetch */

import { fetchGetAgreementsActivity, type AgreementsPayload } from '../agreements';
import {
    fetchGetDwtDefraSubmissionsActivity,
    type DwtDefraSubmissionsPayload,
} from '../dwt-defra-submissions';
import { fetchGetDwtPayloadsActivity, type DwtPayloadsPayload } from '../dwt-payloads';

import {
    fetchMaterialSummaryActivity as executeFetchMaterialSummaryActivity,
    type MaterialSummaryPayload,
} from '../material-summary';
import { ACTIVITY_HANDLER_URL } from './activity-handler.constants';
import { buildActivityRequestHeaders } from './activity-request-headers.util';
import type { AgreementsActivityResult } from './agreements-activity.types';
import type {
    TraceGetUserInfoActivityResult,
    TraceGetUserInfoDataDto,
} from '../user-info/trace-user-info.types';

const GET_USER_INFO_PAYLOAD = {
    activity: 'Get-UserInfo' as const,
    data: {} as Record<string, never>,
    originalActivity: null,
    isAsync: false,
    externalId: null,
};

export class TraceActivityHandler {
    constructor(private readonly activityHandlerUrl: string = ACTIVITY_HANDLER_URL) {}

    /** Delegates to agreements feature transport ({@link fetchGetAgreementsActivity}). */
    async fetchGetAgreements(
        accessToken: string,
        payload: AgreementsPayload
    ): Promise<AgreementsActivityResult> {
        return fetchGetAgreementsActivity(accessToken, payload, this.activityHandlerUrl);
    }

    /** Delegates to material-summary feature transport ({@link executeFetchMaterialSummaryActivity}). */
    async fetchMaterialSummaryActivity(
        accessToken: string,
        payload: MaterialSummaryPayload
    ): Promise<AgreementsActivityResult> {
        return executeFetchMaterialSummaryActivity(accessToken, payload, this.activityHandlerUrl);
    }

    /** Delegates to DWT payloads transport ({@link fetchGetDwtPayloadsActivity} / Get-RegulatoryReports). */
    async fetchGetDwtPayloads(
        accessToken: string,
        payload: DwtPayloadsPayload
    ): Promise<AgreementsActivityResult> {
        return fetchGetDwtPayloadsActivity(accessToken, payload, this.activityHandlerUrl);
    }

    /** Delegates to DWT DEFRA submissions transport ({@link fetchGetDwtDefraSubmissionsActivity}). */
    async fetchGetDwtDefraSubmissions(
        accessToken: string,
        payload: DwtDefraSubmissionsPayload
    ): Promise<AgreementsActivityResult> {
        return fetchGetDwtDefraSubmissionsActivity(accessToken, payload, this.activityHandlerUrl);
    }
    /**
     * Current user profile from Activity Handler (`Get-UserInfo`, same as web `UserService.getCurrentUser`).
     */
    async fetchGetUserInfo(accessToken: string): Promise<TraceGetUserInfoActivityResult> {
        try {
            const response = await fetch(this.activityHandlerUrl, {
                method: 'POST',
                headers: buildActivityRequestHeaders(accessToken),
                body: JSON.stringify(GET_USER_INFO_PAYLOAD),
            });

            const body: unknown = await response.json().catch(() => null);
            const data =
                body !== null &&
                typeof body === 'object' &&
                'data' in body &&
                body.data !== null &&
                typeof body.data === 'object'
                    ? (body.data as Record<string, unknown>)
                    : null;

            if (!response.ok || !data) {
                const message =
                    body !== null &&
                    typeof body === 'object' &&
                    'message' in body &&
                    typeof (body as { message: unknown }).message === 'string'
                        ? (body as { message: string }).message
                        : `Request failed with status ${response.status}`;
                return {
                    kind: 'failure',
                    message,
                    httpStatus: response.status,
                    body,
                };
            }

            return { kind: 'success', data: data as TraceGetUserInfoDataDto };
        } catch (error) {
            return {
                kind: 'failure',
                message: error instanceof Error ? error.message : String(error),
                source: 'network',
            };
        }
    }
}

/** Shared handler for the task pane (agreements fetch, user info, etc.). */
export const traceActivityHandler = new TraceActivityHandler();
